// Singularity (skill id 'breaker'; owner 2026-11-02: 'a big orb that it shoots straight… it pulls like a vacuum everybody
// into it like a vortex… surrounded with electricity, red electricity around it. As it's moving forward, it grows and pulls
// in enemies as it's growing… the final animation is it shrinks into non-existence').
// Render-only: reads `sim.sings` (authoritative position, radius, phase) and the 'sing' / 'singCollapse' events, and never
// writes anything back. Pieces, all pooled (no per-frame allocation):
//  - core: an inky black sphere with a slow dark swirl and a thin crimson fresnel rim (normal blending, so it stays black
//    on bright grass), plus a faint additive rim halo and a thin dashed crimson accretion ring spinning round it
//  - ground vortex: a flat logarithmic-spiral disc (dark red arms, normal blending) under the orb, sized to the pull reach
//    and rotated so the arms flow inward; dark-red dust motes spiral in along it and up into the orb
//  - red electricity: crisp Lightning ribbons crawling over the shell and lashing out to the foes being pulled
//  - collapse: the sim shrinks the radius; motes and arcs rush in harder, then a tight crimson implosion flash, a white
//    pinch star, a thin shock ring and short radial bolts (no big bloom wash)
//  - Collapse Point (evolved; owner 2026-11-03 'make the singularity evolved collapse on itself' + 'let it reach the end
//    point of its path and sit there for a couple seconds, absorbing everybody in'), on top of the above:
//     . hold (sim phase 3): the core pulses and swells a little, a crimson rim shell brightens, light streaks spiral into the
//       centre, the dust vortex runs twice as dense and fast, dark cracks creep across the ground toward it, and foes in reach
//       are drawn stretched toward the core and turned (squeeze(), read by the view's foe instancing; render-only)
//     . each collapse: the core and rim crush inward (rim tightening and brightening), streaks + debris spiral in hard, a
//       space-warp ring contracts onto the point, then a short bright pop; it visibly swells back out (the sim re-forms it)
//       and the second collapse is bigger
import * as THREE from 'three/webgpu';
import { abs, atan, cameraPosition, color, dot, float, fract, length, log, mix, mx_noise_float, mx_worley_noise_float, normalize, normalView, normalWorld, oneMinus, positionViewDirection, positionWorld, sin, smoothstep, step, time, uv, vec2, vec3 } from 'three/tsl';
import type { Sim, SimEvent, Singularity } from '../core/sim';
import { FLASH, type Fx } from './fx';
import type { Lightning } from './lightning';
import { SING_COLLAPSE } from '../core/skillStats';
import { Cards, Decals } from './skillsBShared';

const MAX = 6, MOTES = 420;
const ZAP = 0xe0001c, ZAP_EVO = 0xff1050; // deep red edges so the white-cored bolts read red, not pink
const DUST = [0x12030a, 0x3a0510, 0x6a0812, 0xa00c1c, 0xd81430];
const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _q = new THREE.Quaternion(), _e = new THREE.Euler(), _c = new THREE.Color();
const UP = new THREE.Vector3(0, 1, 0);

/** Collapse Point space-warp ring (camera-facing card): a thin hot band at the card edge with a faint violet inner
 *  shimmer. a = (alpha, heat 0..1 -> whiter) */
function warpLook(a: any): THREE.MeshBasicNodeMaterial { // eslint-disable-line @typescript-eslint/no-explicit-any
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false, blending: THREE.AdditiveBlending });
  const rr = length(uv().sub(vec2(0.5, 0.5))).mul(2), band = smoothstep(0.035, 0.0, abs(rr.sub(0.9)));
  const inner = smoothstep(0.7, 0.88, rr).mul(smoothstep(0.92, 0.86, rr)).mul(0.12); // a thin shimmer just inside the band, not a disc
  m.colorNode = mix(color(0xff1840), color(0xffd8e0), a.y).mul(band.mul(1.4)).add(color(0x7a2cff).mul(inner)).mul(a.x);
  return m;
}
/** ground cracks creeping in toward the Collapse Point (decal). a = (alpha, grow 0..1 from the rim inward, heat -> crimson glow) */
function crackLook(a: any): THREE.MeshBasicNodeMaterial { // eslint-disable-line @typescript-eslint/no-explicit-any
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false });
  const q = uv().sub(vec2(0.5, 0.5)), rr = length(q).mul(2), ang = atan(q.y, q.x).div(Math.PI * 2);
  const N = 11, wig = mx_noise_float(vec3(rr.mul(5), ang.mul(N).floor(), 4.1)).mul(0.24);
  const lane = abs(fract(ang.mul(N).add(wig)).sub(0.5)).mul(rr).mul(Math.PI * 2 / N);
  const wid = float(0.004).add(rr.mul(0.014));
  const inner = float(1).sub(a.y.mul(0.92)); // the cracks run from the rim (1) in toward the centre as `grow` rises
  const crk = smoothstep(wid, wid.mul(0.35), lane).mul(step(inner, rr)).mul(step(rr, 0.98));
  m.colorNode = mix(vec3(0.07, 0.02, 0.03), vec3(0.9, 0.06, 0.12), a.z.mul(smoothstep(0.98, 0.3, rr)));
  m.opacityNode = crk.mul(0.92).mul(a.x);
  return m;
}

interface Look { id: number; maxR: number; spin: number; seen: boolean; x: number; z: number; y: number; gy: number; r: number; reach: number; phase: number; evo: boolean;
  /** ground swirl centre: trails behind the orb while it flies (owner 2026-11-02), slides back under it for the collapse */ vx: number; vz: number; vgy: number; off: number;
  /** Collapse Point: hold progress 0..1, collapse progress 0..1, re-form progress 0..1, and whether this is the last collapse */ hk: number; ck: number; rk: number; last: boolean }
interface Mote { on: boolean; id: number; ang: number; rad: number; h0: number; size: number; col: number; w: number; fade: number }

export class SingularityFx {
  private core: THREE.InstancedMesh; private halo: THREE.InstancedMesh; private ring: THREE.InstancedMesh; private vortex: THREE.InstancedMesh;
  private dust: THREE.InstancedMesh;
  private looks = new Map<number, Look>();
  private motes: Mote[] = [];
  private zapAcc = 0;
  // Collapse Point (evolved) pieces: all pooled and built here, so the warm-up compiles them with the rest
  private rim: THREE.InstancedMesh; private warp: Cards; private cracks: Decals;
  private crk: { on: boolean; id: number; x: number; z: number; y: number; r: number; grow: number; heat: number; fade: number }[] = [];
  private streakAcc = 0; private pts: number[] = [];
  private tNow = 0;
  /** squeeze() result: the yaw that turns a drawn foe to face the nearest Collapse Point */
  sqYaw = 0;

  constructor(scene: THREE.Scene, private bolts: Lightning, private fx: Fx, private groundAt: (x: number, z: number) => number) {
    // core (owner 2026-11-02: 'an infinite deep space looking texture, like a purpleish black hue with stars that look like
    // you're looking into deep space'): the sphere is a window, not a painted ball. Stars and nebula are looked up by the
    // world-space VIEW RAY (camera → surface), so they sit at infinity: as the orb drifts across the field the starfield
    // stays put behind it, like looking through a hole into space. A second, nearer layer is bent by the surface normal
    // (a little gravitational-lens warp toward the rim) so there is depth between the layers. Purple-black, violet rim.
    const cm = new THREE.MeshBasicNodeMaterial({ fog: false });
    const fr = oneMinus(abs(dot(normalView, positionViewDirection)));
    const ray = normalize(positionWorld.sub(cameraPosition));
    const drift = vec3(time.mul(0.012), time.mul(-0.007), time.mul(0.009));          // the deep field turns very slowly
    // the orb only covers a small slice of the view (~0.1 rad), so the far field is sampled at ×9 to get a dense sky across it
    const far = ray.mul(9).add(drift.mul(9)), near = normalize(ray.add(normalWorld.mul(0.35))).mul(2).add(drift.mul(5));
    // stars: Worley feature points (tight cores), brightness varied per cell so most are dim and a few are bright
    const starLayer = (dir: typeof ray, scale: number, size: number, seed: number) => {
      const q = dir.mul(scale).add(vec3(seed, seed * 0.7, seed * 1.3));
      const d = mx_worley_noise_float(q, float(0.9));
      const lum = mx_noise_float(q.mul(0.37)).mul(0.5).add(0.5);
      const tw = sin(time.mul(2.6).add(lum.mul(40))).mul(0.25).add(0.75);          // gentle twinkle
      return smoothstep(float(size), float(0), d).mul(smoothstep(0.3, 0.8, lum)).mul(tw);
    };
    // owner reference board (2026-11-02): dense star fields, colourful nebula clouds (violet, deep blue, magenta, a little
    // teal) over black, a few big bright stars that bloom. Three star layers (far fine dust, mid, near bright + lensed).
    const stars = starLayer(far, 9, 0.3, 5.3).mul(0.9).add(starLayer(far, 4.5, 0.24, 0).mul(1.6)).add(starLayer(near, 9, 0.16, 13.1).mul(2.6));
    const n1 = mx_noise_float(far.mul(0.35)).mul(0.5).add(0.5), n2 = mx_noise_float(near.mul(1.6).add(vec3(3.1, 0, 0))).mul(0.5).add(0.5);
    const n3 = mx_noise_float(far.mul(0.9).add(vec3(0, 9.2, 0))).mul(0.5).add(0.5), wisp = mx_noise_float(far.mul(2.2)).mul(0.5).add(0.5);
    const sp0 = mix(color(0x040010), color(0x5a1ca8), smoothstep(0.38, 0.8, n1).mul(0.9));                 // violet cloud banks
    const sp1 = mix(sp0, color(0x1c46b0), smoothstep(0.45, 0.85, n2).mul(0.8));                                    // deep blue
    const sp2 = mix(sp1, color(0xb0247e), smoothstep(0.55, 0.88, n3).mul(smoothstep(0.35, 0.65, n1)).mul(0.8));     // magenta inside the violet
    const sp3 = mix(sp2, color(0x14909c), smoothstep(0.7, 0.95, n2.mul(n3).mul(1.6)).mul(0.55));                  // a little teal
    const space = sp3.mul(wisp.mul(0.6).add(0.7))                                                                       // fine wispy structure
      .mul(smoothstep(0.0, 0.8, fr).mul(0.4).add(0.6));                                                                // the middle sinks into black
    const starCol = mix(mix(color(0xc8d4ff), color(0xffe6c8), n3), color(0xffffff), n2.mul(0.6));
    // owner 2026-11-02: 'put that swirl inside of the orb itself, to look like a vacuum sucking in the enemies' — the vortex
    // now lives in the orb: log-spiral arms seen face-on (view-space normal → disc coords centred on the orb), flowing
    // INWARD over time and tightening toward a pitch-black eye; the starfield behind them is dimmed so the arms read.
    const nd = normalView.xy, vr = length(nd).max(0.001), vth = atan(nd.y, nd.x);
    const flow = vth.mul(3).add(log(vr).mul(5.5)).add(time.mul(5));                 // constant phase moves to smaller r: inward
    const armN = mx_noise_float(vec3(nd.mul(3), time.mul(0.6))).mul(0.6);            // ragged, not a perfect pinwheel
    const arm = sin(flow.add(armN)).mul(0.5).add(0.5);
    const armBody = smoothstep(0.45, 0.75, arm).mul(smoothstep(0.08, 0.35, vr)).mul(smoothstep(1.0, 0.75, vr));
    const armCrest = smoothstep(0.86, 0.96, arm).mul(smoothstep(0.12, 0.4, vr)).mul(smoothstep(0.98, 0.7, vr));
    const eye = smoothstep(0.32, 0.05, vr);                                           // the black centre everything falls into
    const armCol = mix(color(0x6a24c8), color(0xd070ff), armCrest);
    const deep = space.add(starCol.mul(stars)).mul(float(1).sub(armBody.mul(0.45))).mul(float(1).sub(eye.mul(0.92)));
    cm.colorNode = deep.add(armCol.mul(armBody.mul(0.75).add(armCrest.mul(0.9))).mul(float(1).sub(eye)))
      .add(color(0xb24cff).mul(smoothstep(0.8, 0.97, fr).mul(0.9)));              // thin violet event-horizon rim
    this.core = new THREE.InstancedMesh(new THREE.SphereGeometry(1, 36, 24), cm, MAX);
    // rim halo: a slightly larger shell that only shows at the silhouette (additive, kept low so bloom stays tight)
    const hm = new THREE.MeshBasicNodeMaterial({ fog: false, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
    hm.colorNode = color(0x9a36ff).mul(fr.pow(4).mul(0.9)); // violet rim halo to match the deep-space core
    this.halo = new THREE.InstancedMesh(new THREE.SphereGeometry(1, 28, 18), hm, MAX);
    // thin dashed accretion ring
    const rm = new THREE.MeshBasicNodeMaterial({ fog: false, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    const dash = step(0.35, fract(uv().x.mul(14)));
    rm.colorNode = mix(color(0x8a0a18), color(0xff4a5a), dash).mul(0.85);
    this.ring = new THREE.InstancedMesh(new THREE.TorusGeometry(1, 0.022, 5, 96), rm, MAX);
    // ground vortex: log-spiral arms (dark red, normal blending), dark eye under the orb, soft outer edge
    const vm = new THREE.MeshBasicNodeMaterial({ fog: false, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -4 });
    const d = uv().sub(vec2(0.5, 0.5)), r = length(d).mul(2), th = atan(d.y, d.x);
    // four tight arms with a crisp crimson leading edge over a dark-red body (cel bands, not a soft smear)
    const arms = sin(th.mul(4).add(log(r.add(0.02)).mul(7.0))).mul(0.5).add(0.5);
    const body = smoothstep(0.5, 0.62, arms), crest = smoothstep(0.84, 0.9, arms), edge = smoothstep(1.0, 0.72, r);
    vm.colorNode = mix(color(0x3a0010), color(0xe8102c), crest);
    vm.opacityNode = body.mul(0.55).add(crest.mul(0.35)).mul(edge).add(smoothstep(0.3, 0.0, r).mul(0.55)).min(0.9);
    this.vortex = new THREE.InstancedMesh(new THREE.CircleGeometry(0.5, 72).rotateX(-Math.PI / 2), vm, MAX);
    // dust motes: small crisp discs, camera-facing, normal blending (dark reds read on grass and on dark ground)
    const dm = new THREE.MeshBasicNodeMaterial({ fog: false, transparent: true, depthWrite: false, side: THREE.DoubleSide });
    dm.opacityNode = smoothstep(0.5, 0.4, length(uv().sub(vec2(0.5, 0.5))));
    this.dust = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), dm, MOTES);
    this.dust.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(MOTES * 3), 3);
    for (let i = 0; i < MOTES; i++) this.motes.push({ on: false, id: 0, ang: 0, rad: 0, h0: 0, size: 0, col: 0, w: 0, fade: 0 });
    // Collapse Point rim shell: crimson fresnel, additive; brightness per instance (instanceColor) tightens + brightens it
    const em = new THREE.MeshBasicNodeMaterial({ fog: false, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
    em.colorNode = color(0xff1a3a).mul(fr.pow(2.2).mul(1.3));
    this.rim = new THREE.InstancedMesh(new THREE.SphereGeometry(1, 28, 18), em, MAX);
    this.rim.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(MAX * 3), 3);
    this.rim.renderOrder = 3; this.rim.frustumCulled = false; this.rim.count = 0; scene.add(this.rim);
    this.warp = new Cards(scene, MAX, warpLook, 4);
    this.cracks = new Decals(scene, MAX, crackLook, 1);
    for (let i = 0; i < MAX; i++) this.crk.push({ on: false, id: -1, x: 0, z: 0, y: 0, r: 1, grow: 0, heat: 0, fade: 0 });
    this.vortex.renderOrder = 1; this.core.renderOrder = 2; this.halo.renderOrder = 3; this.ring.renderOrder = 3; this.dust.renderOrder = 2;
    for (const m of [this.core, this.halo, this.ring, this.vortex, this.dust]) { m.frustumCulled = false; m.count = 0; scene.add(m); }
  }

  event(ev: SimEvent): void {
    if (ev.t === 'sing') {
      const gy = this.groundAt(ev.x, ev.z), y = gy + 0.35 + ev.r;
      this.fx.stars.add(ev.x, y, ev.z, ev.r * 2.2, 0.14, ev.evolved ? ZAP_EVO : ZAP, 1.6);
      this.fx.rings.add(ev.x, gy + 0.12, ev.z, ev.r * 3, 0.3, 0xff2a3a, 1.4, 1);
    } else if (ev.t === 'singHold') {
      // Collapse Point stops: a dark ring snaps in around it and the ground starts cracking toward it
      const gy = this.groundAt(ev.x, ev.z), lk = this.looks.get(ev.id), reach = lk ? lk.reach : 6;
      this.fx.rings.add(ev.x, gy + 0.14, ev.z, reach * 1.1, 0.35, 0x8a0a18, 1.6, 1.2);
      const c = this.crk.find((q) => !q.on) ?? this.crk[0];
      c.on = true; c.id = ev.id; c.x = ev.x; c.z = ev.z; c.y = gy; c.r = reach * 1.15; c.grow = 0; c.heat = 0; c.fade = 1;
    } else if (ev.t === 'singCollapse') {
      const gy = this.groundAt(ev.x, ev.z), lk = this.looks.get(ev.id), y = lk ? lk.y : gy + 1.2, col = ev.evolved ? ZAP_EVO : ZAP;
      if (ev.evolved) this.crushPop(ev.x, y, ev.z, gy, ev.r, ev.last);
      // tight crimson implosion: white pinch, small flash, thin shock ring, short radial bolts, a few sparks
      this.fx.stars.add(ev.x, y, ev.z, 1.6, 0.12, 0xffffff, 2.2);
      this.fx.stars.add(ev.x, y, ev.z, ev.r * 1.1, 0.22, 0xff1a3a, 1.8);
      this.fx.flashes.add(ev.x, y, ev.z, ev.r * 0.3, 0.14, 0xff2040, 1.4);
      this.fx.rings.add(ev.x, gy + 0.15, ev.z, ev.r * 1.15, 0.4, 0xff1030, 5, 0.6);
      this.fx.rings.add(ev.x, y, ev.z, ev.r * 0.7, 0.22, 0xff6070, 1.6, 0.5);
      for (let k = 0; k < 9; k++) {
        const a = (k / 9) * 6.283 + Math.random() * 0.5, L = ev.r * (0.55 + Math.random() * 0.4), pts: number[] = [];
        for (let i = 0; i <= 5; i++) { const f = i / 5; pts.push(ev.x + Math.cos(a) * L * f, y + (gy + 0.3 - y) * f * f, ev.z + Math.sin(a) * L * f); }
        this.bolts.add(pts, col, 0.28, 0.16, 0.7);
      }
      for (let k = 0; k < 16; k++) { const a = Math.random() * 6.283, sp = 4 + Math.random() * 6; this.fx.particles.emitDir(ev.x, y, ev.z, Math.cos(a) * sp, (Math.random() - 0.3) * 4, Math.sin(a) * sp, k % 3 ? 0xff2a3a : 0xffd0d8, 0.28, 0.32, 3); }
      this.fx.lights.flash(ev.x, y, ev.z, 0xff2040, 22, 0.25, ev.r * 2.5);
      this.fx.shake = Math.min(1, this.fx.shake + 0.22);
    }
  }

  /** Collapse Point: crushed to a point. A short bright pop (Reduce flashing scales it through the fx primitives), a
   *  thin ring snapping outward as it re-forms (first collapse) or a wider crimson shock (the last, bigger one) */
  private crushPop(x: number, y: number, z: number, gy: number, r: number, last: boolean): void {
    const big = last ? 1.4 : 1, fx = this.fx;
    fx.stars.add(x, y, z, 2.6 * big, 0.1, 0xffffff, 2.6);
    fx.stars.add(x, y, z, r * 0.6 * big, 0.16, 0xff3050, 2);
    fx.rings.add(x, y, z, r * 0.55 * big, 0.25, 0xffd0d8, 2.2, 0.35);
    if (last) fx.rings.add(x, gy + 0.2, z, r * 1.5, 0.55, 0xff1030, 3, 0.8);
    const n = Math.round((last ? 26 : 14) * this.density());
    for (let k = 0; k < n; k++) { const a = Math.random() * 6.283, sp = (6 + Math.random() * 8) * big; fx.particles.emitDir(x, y, z, Math.cos(a) * sp, (Math.random() - 0.4) * 6, Math.sin(a) * sp, k % 3 ? 0xff2a3a : 0xffffff, 0.22, 0.26, 3); }
    if (FLASH.dim >= 1) fx.lights.flash(x, y, z, 0xff5070, 30 * big, 0.18, r * 2);
    fx.shake = Math.min(1, fx.shake + 0.2 * big);
  }
  private density(): number { return Math.min(1.5, this.fx.particles.capacity / 2200); }

  /**
   * How hard the nearest evolved Singularity is crushing a foe at (x, z) right now: 0 (none) to 1 (at the core, collapsing).
   * Render-only: the view stretches the drawn foe toward the core by this and turns it to face it (sqYaw).
   */
  squeeze(x: number, z: number): number {
    let best = 0;
    for (const lk of this.looks.values()) {
      if (!lk.evo || (lk.phase !== 3 && lk.phase !== 1)) continue;
      const dx = lk.x - x, dz = lk.z - z, d = Math.hypot(dx, dz);
      if (d > lk.reach) continue;
      const k = (1 - d / lk.reach) * (lk.phase === 3 ? 0.35 + 0.65 * lk.hk : 1);
      if (k > best) { best = k; this.sqYaw = Math.atan2(dx, dz) + Math.sin(this.tNow * 3 + x) * 0.3 * k; }
    }
    return best;
  }

  /** a light streak spiralling into the orb from radius R0 (Lightning ribbon, pooled) */
  private spiralStreak(lk: Look, R0: number, col: number, w: number, life: number): void {
    const pts = this.pts; pts.length = 0;
    const a0 = Math.random() * 6.283, turn = (0.55 + Math.random() * 0.45) * 6.283, h0 = (Math.random() - 0.35) * R0 * 0.45;
    for (let i = 0; i <= 8; i++) {
      const f = i / 8, rr = Math.max(lk.r * 0.15, R0 * (1 - f) ** 1.25), a = a0 + f * turn;
      pts.push(lk.x + Math.cos(a) * rr, lk.y + h0 * (1 - f), lk.z + Math.sin(a) * rr);
    }
    this.bolts.add(pts, col, w, life, 0.25);
  }

  /** per-frame Collapse Point pieces for one evolved orb; ri = the rim slot. Returns 1 when it used the rim slot. */
  private collapsePoint(lk: Look, s: Singularity, dt: number, camQ: THREE.Quaternion, ri: number): number {
    const col = ZAP_EVO, big = lk.last ? 1.35 : 1;
    let rimK = 0, rimS = 1.12;
    if (s.phase === 3) {
      lk.hk = Math.min(1, s.pt / Math.max(0.05, s.hold));
      rimK = 0.5 + 0.7 * lk.hk + 0.25 * Math.sin(this.tNow * 6.283 * Math.min(1.5 + 2 * lk.hk, FLASH.L.maxHz)) * FLASH.L.flicker;
      this.streakAcc += dt * (5 + 12 * lk.hk) * this.density();
    } else if (s.phase === 1) {
      lk.ck = Math.min(1, s.pt / SING_COLLAPSE);
      rimK = 1 + 2.6 * lk.ck; rimS = 1.22 - 0.12 * lk.ck; // the rim tightens onto the shrinking core and burns brighter
      this.streakAcc += dt * 34 * big * this.density();
      // space-warp ring: contracts from beyond the pull reach onto the point as it crushes
      const u = lk.ck, R = (lk.reach * 1.25 * big) * (1 - u) ** 1.3 + s.r * 1.6;
      this.warp.push(lk.x, lk.y, lk.z, R * 2, camQ, 0.85 * (0.4 + 0.6 * u), u);
    } else if (s.phase === 2) {
      lk.rk = Math.min(1, s.pt / 0.5); rimK = 0.9 * (1 - lk.rk) + 0.5; // swelling back out of the point
    }
    while (this.streakAcc >= 1) { this.streakAcc -= 1; this.spiralStreak(lk, lk.reach * (0.7 + Math.random() * 0.5) * (s.phase === 1 ? big : 1), Math.random() < 0.3 ? 0xffe0e6 : col, 0.12 + s.r * 0.03, 0.14); }
    if (rimK > 0 && s.r > 0.02) {
      _p.set(lk.x, lk.y, lk.z); _s.setScalar(s.r * rimS);
      this.rim.setMatrixAt(ri, _m.compose(_p, _q.identity(), _s));
      const C = this.rim.instanceColor!.array as Float32Array, k = rimK * FLASH.dim ** 0.5;
      C[ri * 3] = k; C[ri * 3 + 1] = k * (0.25 + 0.2 * lk.ck); C[ri * 3 + 2] = k * (0.3 + 0.2 * lk.ck);
      return 1;
    }
    return 0;
  }

  update(sim: Sim, dt: number, cam: THREE.Camera): void {
    this.tNow += dt;
    this.warp.begin(); this.cracks.begin();
    let rn = 0;
    for (const lk of this.looks.values()) lk.seen = false;
    let n = 0;
    for (const s of sim.sings) {
      let lk = this.looks.get(s.id);
      if (!lk) { lk = { id: s.id, maxR: 0, spin: Math.random() * 6.283, seen: true, x: 0, z: 0, y: 0, gy: 0, r: 0, reach: 0, phase: 0, evo: false, vx: s.x, vz: s.z, vgy: 0, off: 0, hk: 0, ck: 0, rk: 0, last: false }; this.looks.set(s.id, lk); }
      this.place(lk, s, dt);
      if (s.evo) { lk.last = s.left <= 1; if (rn < MAX) rn += this.collapsePoint(lk, s, dt, cam.quaternion, rn); }
      if (n >= MAX || s.r < 0.02) continue;
      // core + halo + ring (Collapse Point hold: the core pulses and swells a little as it feeds)
      const pulse = s.evo && s.phase === 3 ? 1 + 0.08 * lk.hk + 0.04 * Math.sin(this.tNow * 6.283 * Math.min(1.2 + 1.6 * lk.hk, FLASH.L.maxHz)) * FLASH.L.flicker : 1;
      _p.set(lk.x, lk.y, lk.z); _s.setScalar(s.r * pulse);
      this.core.setMatrixAt(n, _m.compose(_p, _q.identity(), _s));
      _s.setScalar(s.r * 1.1); this.halo.setMatrixAt(n, _m.compose(_p, _q, _s));
      _e.set(Math.PI / 2 - 0.42, lk.spin * 0.6, 0.18 * Math.sin(lk.spin * 0.7)); _q.setFromEuler(_e); _s.setScalar(s.r * 1.42);
      this.ring.setMatrixAt(n, _m.compose(_p, _q, _s));
      // ground vortex: spins so its log-spiral arms flow inward (see the notes in update of the motes)
      _p.set(lk.vx, lk.vgy + 0.07, lk.vz); _q.setFromAxisAngle(UP, -lk.spin); _s.set(lk.reach * 2.1, 1, lk.reach * 2.1);
      this.vortex.setMatrixAt(n, _m.compose(_p, _q, _s));
      n++;
      this.zap(sim, s, lk, dt);
    }
    for (const m of [this.core, this.halo, this.ring]) { m.count = n; if (n) m.instanceMatrix.needsUpdate = true; }
    this.rim.count = rn; if (rn) { this.rim.instanceMatrix.needsUpdate = true; this.rim.instanceColor!.needsUpdate = true; }
    // ground cracks: creep in toward the centre over the hold, glow crimson through the collapses, then fade out
    for (const c of this.crk) {
      if (!c.on) continue;
      const lk = this.looks.get(c.id);
      if (lk && lk.phase === 3) { c.grow = lk.hk; c.heat = 0.3 * lk.hk; }
      else if (lk) { c.grow = 1; c.heat = Math.max(c.heat, lk.phase === 1 ? 0.4 + 0.6 * lk.ck : 0.4); }
      else { c.heat = Math.max(0, c.heat - dt * 1.5); c.fade -= dt / 1.6; if (c.fade <= 0) { c.on = false; continue; } }
      this.cracks.push(c.x, c.y + 0.05, c.z, c.r * 2, c.id * 1.3, Math.min(1, c.fade), c.grow, c.heat);
    }
    this.warp.end(); this.cracks.end();
    this.vortex.count = 0; // owner 2026-11-02: the swirl moved off the ground and into the orb (core shader)
    for (const [id, lk] of this.looks) if (!lk.seen) this.looks.delete(id);
    this.updateMotes(dt, cam);
  }

  private place(lk: Look, s: Singularity, dt: number): void {
    lk.seen = true; lk.maxR = Math.max(lk.maxR, s.r); lk.phase = s.phase; lk.evo = s.evo;
    lk.x = s.x; lk.z = s.z; lk.r = s.r; lk.reach = Math.max(0.01, s.r * s.pullMul);
    lk.gy = this.groundAt(s.x, s.z);
    lk.y = lk.gy + 0.35 + lk.maxR; // floats just clear of the ground; keeps its height while it shrinks away
    lk.spin += dt * (s.phase === 1 ? 6 : 2.4);
    // owner 2026-11-02: 'position the swirl that is underneath to be behind the orb as it moves forward' — a wake: the
    // swirl's centre sits behind the orb along its flight so the orb rides its leading edge; eases back under it to collapse
    const want = 0; // the swirl now lives inside the orb (owner 2026-11-02), so dust gathers straight under it
    lk.off += (want - lk.off) * Math.min(1, dt * (s.phase === 0 ? 10 : 5));
    lk.vx = s.x - s.dx * lk.off; lk.vz = s.z - s.dz * lk.off; lk.vgy = this.groundAt(lk.vx, lk.vz);
  }

  /** red electricity crawling over the shell, and arcs lashing out to the foes it is dragging in */
  private zap(sim: Sim, s: Singularity, lk: Look, dt: number): void {
    const col = s.evo ? ZAP_EVO : ZAP, collapsing = s.phase === 1;
    const shell = 2 + (collapsing ? 1 : 0) + (s.r > 1.5 ? 1 : 0);
    for (let k = 0; k < shell; k++) if (Math.random() < 0.75) this.shellArc(lk.x, lk.y, lk.z, s.r * 1.06, col);
    // tendrils: short forked bolts leaping off the shell into the air around it
    if (!collapsing && Math.random() < 0.5) {
      const th = Math.random() * 6.283, ph = 0.4 + Math.random() * 1.6, L = s.r * (0.5 + Math.random() * 0.7), pts: number[] = [];
      const nx = Math.sin(ph) * Math.cos(th), ny = Math.cos(ph), nz = Math.sin(ph) * Math.sin(th);
      for (let i = 0; i <= 4; i++) { const d = s.r + L * i / 4; pts.push(lk.x + nx * d, lk.y + ny * d, lk.z + nz * d); }
      this.bolts.add(pts, col, 0.2 + s.r * 0.05, 0.08, 0.5);
    }
    // arcs to pulled foes: a few per frame, random picks within the pull reach
    let budget = collapsing ? 2 : 3;
    const R2 = lk.reach * lk.reach;
    for (const e of sim.enemies) {
      if (budget <= 0) break;
      if (!e.alive) continue;
      const dx = e.x - lk.x, dz = e.z - lk.z, d2 = dx * dx + dz * dz;
      if (d2 > R2 || d2 < s.r * s.r || Math.random() > 0.12) continue;
      budget--;
      const d = Math.sqrt(d2), ux = dx / d, uz = dz / d, ey = this.groundAt(e.x, e.z) + 0.9, pts: number[] = [];
      const sx = lk.x + ux * s.r, sy = lk.y + (Math.random() - 0.5) * s.r * 0.6, sz = lk.z + uz * s.r;
      for (let i = 0; i <= 4; i++) { const f = i / 4; pts.push(sx + (e.x - sx) * f, sy + (ey - sy) * f + Math.sin(f * Math.PI) * 0.4, sz + (e.z - sz) * f); }
      this.bolts.add(pts, col, 0.2, 0.09, 0.6);
    }
    // spawn dust motes at the rim of the pull
    this.zapAcc += dt * (40 + lk.reach * 10) * (collapsing ? (s.evo ? 1.2 : 0.4) : s.evo && s.phase === 3 ? 2.2 : 1) * Math.min(1, this.density()); // Collapse Point: dense debris pouring in
    while (this.zapAcc >= 1) { this.zapAcc -= 1; this.spawnMote(lk); }
  }

  private shellArc(cx: number, cy: number, cz: number, r: number, col: number): void {
    let th = Math.random() * 6.283, ph = 0.2 + Math.random() * 2.7;
    const steps = 6 + Math.floor(Math.random() * 5), dth = (Math.random() < 0.5 ? -1 : 1) * (0.12 + Math.random() * 0.12), pts: number[] = [];
    for (let i = 0; i <= steps; i++) {
      const rr = r * (1 + (Math.random() - 0.3) * 0.14);
      pts.push(cx + Math.sin(ph) * Math.cos(th) * rr, cy + Math.cos(ph) * rr, cz + Math.sin(ph) * Math.sin(th) * rr);
      th += dth + (Math.random() - 0.5) * 0.25; ph = Math.max(0.1, Math.min(3.0, ph + (Math.random() - 0.5) * 0.35));
    }
    this.bolts.add(pts, col, 0.16 + r * 0.06, 0.1, 0.35 + r * 0.1);
  }

  private spawnMote(lk: Look): void {
    const m = this.motes.find((q) => !q.on); if (!m) return;
    m.on = true; m.id = lk.id; m.ang = Math.random() * 6.283; m.rad = lk.reach * (0.75 + Math.random() * 0.3);
    m.h0 = 0.05 + Math.random() * 0.5; m.size = 0.1 + Math.random() * 0.16; m.col = DUST[Math.floor(Math.random() * DUST.length)];
    m.w = 0.8 + Math.random() * 0.5; m.fade = 0;
  }

  /** Motes orbit with increasing angle (atan2(z, x)) and fall inward, matching the vortex disc's arm flow, and lift into
   *  the orb as they near it; the collapse drags them in three times faster. */
  private updateMotes(dt: number, cam: THREE.Camera): void {
    const col = this.dust.instanceColor!.array as Float32Array;
    let n = 0;
    for (const m of this.motes) {
      if (!m.on) continue;
      const lk = this.looks.get(m.id);
      if (!lk) { m.on = false; continue; }
      const k = lk.phase === 1 ? 3 : lk.evo && lk.phase === 3 ? 2 : 1; // Collapse Point hold: a vortex twice as fast
      m.ang += dt * m.w * (1.6 + 5 / Math.max(0.4, m.rad)) * k;
      m.rad -= dt * (1.4 + lk.reach * 0.35) * m.w * k;
      m.fade = Math.min(1, m.fade + dt * 5);
      const inner = Math.max(0.15, lk.r * 0.85);
      if (m.rad <= inner) { m.on = false; continue; }
      const u = Math.max(0, Math.min(1, 1 - (m.rad - inner) / Math.max(0.1, lk.reach - inner))); // 0 at the rim, 1 at the orb
      const y = lk.vgy + m.h0 + (lk.y - lk.vgy - m.h0) * u * u;
      const cx = lk.vx + (lk.x - lk.vx) * u, cz = lk.vz + (lk.z - lk.vz) * u; // spiral round the swirl, then up into the orb
      _p.set(cx + Math.cos(m.ang) * m.rad, y, cz + Math.sin(m.ang) * m.rad);
      const sz = m.size * (0.5 + 0.5 * m.fade) * (1 - 0.4 * u);
      _s.set(sz * 1.6, sz, sz); // a little streaked along the swirl
      _q.copy(cam.quaternion);
      this.dust.setMatrixAt(n, _m.compose(_p, _q, _s));
      _c.setHex(m.col); col[n * 3] = _c.r; col[n * 3 + 1] = _c.g; col[n * 3 + 2] = _c.b;
      n++;
    }
    this.dust.count = n;
    if (n) { this.dust.instanceMatrix.needsUpdate = true; this.dust.instanceColor!.needsUpdate = true; }
  }

  clear(): void {
    this.looks.clear(); for (const m of this.motes) m.on = false;
    for (const m of [this.core, this.halo, this.ring, this.vortex, this.dust, this.rim]) m.count = 0;
    this.warp.clear(); this.cracks.clear(); for (const c of this.crk) c.on = false; this.streakAcc = 0;
  }
}
